import { FieldArray, Formik } from 'formik';
import Toggle from '../../../../components/toggle/Toggle';
import classNames from 'classnames';
import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect } from 'react';
import Selector from '../../../../components/selector/Selector';
import Textarea from '../../../../components/input/Textarea';
import ButtonIcon from '../../../../components/button/Button.icon';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../util/vars';
import Button from '../../../../components/button/Button';
import Tabs from '../../../../components/tabs/Tabs';
import * as Yup from 'yup';
import type { OptionType } from '../../../../util/types';
import type { PreviewData } from './OrganizationEmailPreview';
import type { OrganizationWeeklyLeadersEmail, PutOrganizationWeeklyLeadersEmailRequestBody } from '@whizz/api-model';
import { useI18n } from '@whizz/react-i18n';
import { DateTime, Info } from 'luxon';
import {
  getNextWeekday,
  getPrevWeekday,
  timezoneWeekdayAndHourToUtc,
  utcWeekdayAndHourToTimeZone,
} from '../../../../util/dates';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { repository } from '@whizz/api-client';
import type { TFunction } from 'i18next';
import { ChoiceGroup } from '../../../../components';
import translations from '../../../../translations';

const VALIDATION_SCHEMA = (t: TFunction) => Yup.object().shape({
  isEnabled: Yup.boolean().defined(),
  hour: Yup.mixed().required(t('forms.errors.required')),
  weekday: Yup.mixed().required(t('forms.errors.required')),
  to: Yup.array().of(Yup.mixed<OptionType<number>>()),
  cc: Yup.array().of(Yup.object().shape({
    label: Yup.string(),
    value: Yup.string().email(),
  })),
  bcc: Yup.array().of(Yup.object().shape({
    label: Yup.string(),
    value: Yup.string().email(),
  })),
  isEnabledOneOffMessage: Yup.boolean(),
  standardGreeting: Yup.boolean(),
  standardMessage: Yup.string().required(t('forms.errors.required')),
  oneOffMessage: Yup.string()
    .when('isEnabledOneOffMessage', {
      is: true,
      then: (schema) => schema.required(t('forms.errors.required')),
    }),
  isEnabledLearningOutcomeReminderMessage: Yup.boolean(),
  learningOutcomeReminderMessage: Yup.string(),
  reportingPeriodFrom: Yup.mixed().required(t('forms.errors.required')),
  reportingPeriodTo: Yup.mixed().required(t('forms.errors.required')),
  metricsComponents: Yup.array().of(Yup.mixed<OptionType<string>>()),
});

const METRICS_COMPONENTS = [
  'usage',
  'progressions',
  'hall_of_fame',
];

const DEFAULT_VALUES = {
  hour: 8,
  weekday: 1,
  reportingPeriodFrom: 1,
  reportingPeriodTo: 7,
  metricsComponents: METRICS_COMPONENTS as OrganizationWeeklyLeadersEmail['metricsComponents'],
};

const WEEKDAYS_OPTIONS = Info.weekdays().map((weekday, index) => ({ label: weekday, value: index + 1 }));

const OrganizationEmailConfigurationForm = ({ email, setPreviewData }: Props) => {
  const { translate: t } = useI18n();
  const queryClient = useQueryClient();

  const now = DateTime.now().setZone(email.organization.timezone);

  const getOrganizationContactsQuery = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_ORGANISATION_CONTACTS, { id: email.organization.id } ],
    queryFn: () => repository.AdminRepository.getOrganizationContacts(email.organization.id),
  });

  const putOrganizationWeeklyLeadersEmailMutation = useMutation({
    mutationFn: ({ data }: { data: PutOrganizationWeeklyLeadersEmailRequestBody }) =>
      repository.AdminRepository.putOrganizationWeeklyLeadersEmail(email.organization.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ QUERIES_IDS.ADMIN.GET_ORGANISATION_WEEKLY_LEADERS_EMAIL ], exact: false });
      queryClient.invalidateQueries({ queryKey: [ QUERIES_IDS.ADMIN.GET_ORGANISATIONS ], exact: false });
    },
  });

  const orgLocalWeekdayAndHour = null !== email.weekday && null !== email.hour
    ? utcWeekdayAndHourToTimeZone(email.weekday, email.hour, email.organization.timezone)
    : null;

  const initialMessage = email.standardMessage ?? ((translations[email.organization.locale as keyof typeof translations] ?? translations['en-gb'])).pages.ADMIN.ORGANIZATIONS.FORM.INITIAL_VALUES.STANDARD_MESSAGE;

  return (
    <div className={'flex flex-col gap-5'}>
      <Formik<FormValues>
        validationSchema={VALIDATION_SCHEMA(t)}
        initialValues={{
          isEnabled: email.isEnabled ?? false,
          hour: null != orgLocalWeekdayAndHour
            ? { label: `${orgLocalWeekdayAndHour.hour}:00`, value: orgLocalWeekdayAndHour.hour }
            : { label: `${DEFAULT_VALUES.hour}:00`, value: DEFAULT_VALUES.hour },
          weekday: null !== orgLocalWeekdayAndHour
            ? { label: Info.weekdays()[orgLocalWeekdayAndHour.weekday - 1], value: orgLocalWeekdayAndHour.weekday }
            : { label: Info.weekdays()[DEFAULT_VALUES.weekday - 1], value: DEFAULT_VALUES.weekday },
          to: email.contacts?.map((contact) => ({
            label: `${contact.firstname} ${contact.lastname}`,
            value: contact.id,
            firstName: contact.firstname,
          })) ?? [],
          cc: email.cc?.map((item) => ({ label: item, value: item })) ?? [],
          bcc: email.bcc?.map((item) => ({ label: item, value: item })) ?? [],
          messageTab: (email.isEnabledOneOffMessage ?? false) ? MessageTab.oneOff : MessageTab.standard,
          isEnabledOneOffMessage: email.isEnabledOneOffMessage ?? false,
          standardGreeting: email.standardGreeting ?? true,
          standardMessage: initialMessage,
          oneOffMessage: email.oneOffMessage ?? '',
          isEnabledLearningOutcomeReminderMessage: email.isEnabledLearningOutcomeReminderMessage ?? false,
          learningOutcomeReminderMessage: email.learningOutcomeReminderMessage ?? '',
          reportingPeriodFrom: null !== email.reportingPeriodFrom
            ? { label: Info.weekdays()[email.reportingPeriodFrom - 1], value: email.reportingPeriodFrom }
            : { label: Info.weekdays()[DEFAULT_VALUES.reportingPeriodFrom - 1], value: DEFAULT_VALUES.reportingPeriodFrom },
          reportingPeriodTo: null !== email.reportingPeriodTo
            ? { label: Info.weekdays()[email.reportingPeriodTo - 1], value: email.reportingPeriodTo }
            : { label: Info.weekdays()[DEFAULT_VALUES.reportingPeriodTo - 1], value: DEFAULT_VALUES.reportingPeriodTo },
          metricsComponents: (
            null != email.id ? email.metricsComponents : DEFAULT_VALUES.metricsComponents
          ).map((metricsComponent) => ({
            label: t(`pages.ADMIN.FORM.LABELS.METRICS_COMPONENTS_OPTIONS.${metricsComponent}`),
            value: metricsComponent,
          })),
        }}
        onSubmit={(values) => {
          const {
            isEnabled, hour, weekday, to, cc, bcc,
            isEnabledOneOffMessage, standardGreeting, standardMessage, oneOffMessage,
            isEnabledLearningOutcomeReminderMessage, learningOutcomeReminderMessage,
            reportingPeriodFrom, reportingPeriodTo, metricsComponents,
          } = values;

          const utcWeekdayHour = timezoneWeekdayAndHourToUtc(weekday!.value, hour!.value, email.organization.timezone);

          putOrganizationWeeklyLeadersEmailMutation.mutate({
            data: {
              isEnabled,
              hour: utcWeekdayHour.hour,
              weekday: utcWeekdayHour.weekday,
              to: to.map((item) => item.value),
              cc: cc.map((item) => item.value),
              bcc: bcc.map((item) => item.value),
              isEnabledOneOffMessage,
              standardGreeting,
              standardMessage: standardMessage!,
              oneOffMessage,
              isEnabledLearningOutcomeReminderMessage,
              learningOutcomeReminderMessage: learningOutcomeReminderMessage!,
              reportingPeriodFrom: reportingPeriodFrom!.value,
              reportingPeriodTo: reportingPeriodTo!.value,
              metricsComponents: metricsComponents
                .filter((c) => null != c)
                .map((c) => c!.value),
            },
          });
        }}
      >
        {({ values, errors, setFieldValue, handleSubmit }) => {
          let nextWeekday: DateTime | null = null;
          let reportingPeriodFromDate: DateTime | null = null;
          let reportingPeriodToDate: DateTime | null = null;

          if (null != values.weekday && null !== values.hour) {
            let dateFrom = now;

            if (null != email.pausedUntil) {
              const pausedUntil = DateTime.fromISO(email.pausedUntil);
              if (now < pausedUntil) {
                dateFrom = pausedUntil;
              }
            }

            nextWeekday = getNextWeekday(values.weekday.value, values.hour.value, dateFrom);

            if (null != values.reportingPeriodFrom && null != values.reportingPeriodTo) {
              reportingPeriodToDate = getPrevWeekday(values.reportingPeriodTo.value, 23, nextWeekday);
              reportingPeriodFromDate = getPrevWeekday(values.reportingPeriodFrom.value, 0, reportingPeriodToDate);
            }
          }

          useEffect(() => {
            setPreviewData((prev) => ({
              ...prev,
              message: values.isEnabledOneOffMessage ? values.oneOffMessage : values.standardMessage,
            }));
          }, [ values.standardMessage, values.oneOffMessage, values.isEnabledOneOffMessage ]);

          useEffect(() => {
            if (null === reportingPeriodFromDate || null === reportingPeriodToDate) {
              setPreviewData((prev) => ({ ...prev, startDate: null, endDate: null }));
              return;
            }

            setPreviewData((prev) => ({
              ...prev,
              startDate: reportingPeriodFromDate ?? null,
              endDate: reportingPeriodToDate ?? null,
            }));
          }, [ reportingPeriodFromDate?.toLocaleString(), reportingPeriodToDate?.toLocaleString() ]);

          useEffect(() => {
            setPreviewData((prev) => ({
              ...prev,
              metricsComponents: values.metricsComponents.filter((o) => null != o).map((o) => o!.value),
            }));
          }, [ values.metricsComponents ]);

          return (
            <form onSubmit={handleSubmit}>
              <div className={'flex flex-col gap-5'}>
                <div>
                  <Button
                    type={'submit'}
                    color={'positive'}
                    className={'w-full'}
                    disabled={putOrganizationWeeklyLeadersEmailMutation.isLoading}
                  >
                    {t('pages.ADMIN.FORM.LABELS.SAVE')}
                  </Button>
                </div>

                <hr className="h-px my-4 bg-gray-300 border-0" />

                <div className={'flex gap-10'}>
                  <div className={'flex gap-3 items-center'}>
                    <Toggle
                      id={'enabled'}
                      label={t('pages.ADMIN.FORM.LABELS.ENABLED')}
                      color={'header'}
                      checked={values.isEnabled}
                      isOnOff
                      onChange={() => {
                        const newValue = !values.isEnabled;
                        setFieldValue('isEnabled', newValue);
                        setPreviewData((prev) => ({ ...prev, isEnabled: newValue }));
                      }}
                    />
                    <button
                      type={'button'}
                      className={'appearance-none'}
                      onClick={() => {
                        const newValue = !values.isEnabled;
                        setFieldValue('isEnabled', newValue);
                        setPreviewData((prev) => ({ ...prev, isEnabled: newValue }));
                      }}
                    >
                      <div className={`font-bold transition-opacity ${classNames({ 'opacity-50': !values.isEnabled })}`}>
                        {t('pages.ADMIN.FORM.LABELS.ENABLED')}
                      </div>
                    </button>
                  </div>
                  {(values.isEnabled && null != nextWeekday) && (
                    <div>
                      <span className={'text-sm'}>
                        Next email goes at {nextWeekday.toFormat('HH:mm')} on {nextWeekday.toLocaleString()}, organisation local time
                      </span>
                    </div>
                  )}
                </div>

                <div className={'grid grid-cols-2 gap-5'}>
                  <Selector
                    id={'hour'}
                    label={t('pages.ADMIN.FORM.LABELS.HOUR')}
                    options={Array.from(Array(24).keys()).map((hour) => ({ label: `${hour}:00`, value: hour }))}
                    onChange={(option: OptionType<number>) => setFieldValue('hour', option)}
                    value={values.hour ?? ''}
                    error={errors.hour}
                  />
                  <Selector
                    id={'weekday'}
                    label={t('pages.ADMIN.FORM.LABELS.WEEKDAY')}
                    options={WEEKDAYS_OPTIONS}
                    onChange={(option: OptionType<number>) => setFieldValue('weekday', option)}
                    value={values.weekday ?? ''}
                    error={errors.weekday}
                  />
                </div>

                <Selector
                  id={'to'}
                  label={t('pages.ADMIN.FORM.LABELS.TO')}
                  placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.TO')}
                  options={(getOrganizationContactsQuery.data?.data ?? []).map((contact) => ({
                    label: `${contact.firstname} ${contact.lastname}`,
                    value: contact.id,
                    firstName: contact.firstname,
                  }))}
                  onChange={(options: (OptionType<number> & { firstName: string })[]) => {
                    setFieldValue('to', options);
                    setPreviewData((prev) => ({
                      ...prev,
                      contacts: options.map((o) => o.firstName),
                    }));
                  }}
                  value={values.to}
                  error={errors.to}
                  isDisabled={getOrganizationContactsQuery.isFetching}
                  isMulti
                />

                <Selector
                  id={'cc'}
                  label={t('pages.ADMIN.FORM.LABELS.CC')}
                  placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.CC')}
                  onChange={(options: OptionType<string>[]) => setFieldValue('cc', options)}
                  value={values.cc}
                  error={0 < (errors.cc?.length ?? 0) ? t('pages.ADMIN.FORM.ERRORS.INVALID_EMAILS') : null}
                  customComponents={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  isCreatable
                  isMulti
                />

                <Selector
                  id={'bcc'}
                  label={t('pages.ADMIN.FORM.LABELS.BCC')}
                  placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.BCC')}
                  onChange={(options: OptionType<string>[]) => setFieldValue('bcc', options)}
                  value={values.bcc}
                  error={0 < (errors.bcc?.length ?? 0) ? t('pages.ADMIN.FORM.ERRORS.INVALID_EMAILS') : null}
                  customComponents={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  isCreatable
                  isMulti
                />

                <hr className="h-px my-4 bg-gray-300 border-0" />

                <h3 className={'font-bold'}>{t('pages.ADMIN.FORM.LABELS.YOUR_GREETING')}</h3>

                <div className="space-y-3">
                  <ChoiceGroup
                    name={'standardGreeting'}
                    id={'standard'}
                    type={'radio'}
                    label={t('pages.ADMIN.FORM.LABELS.STANDARD_GREETING.standard')}
                    checked={values.standardGreeting === true}
                    onChange={() => {
                      setFieldValue('standardGreeting', true);
                      setPreviewData((prev) => ({ ...prev, standardGreeting: true }));
                    }}
                  />
                  <ChoiceGroup
                    name={'standardGreeting'}
                    id={'custom'}
                    type={'radio'}
                    label={t('pages.ADMIN.FORM.LABELS.STANDARD_GREETING.custom')}
                    checked={values.standardGreeting === false}
                    onChange={() => {
                      setFieldValue('standardGreeting', false);
                      setPreviewData((prev) => ({ ...prev, standardGreeting: false }));
                    }}
                  />
                </div>

                <hr className="h-px my-4 bg-gray-300 border-0" />

                <h3 className={'font-bold'}>{t('pages.ADMIN.FORM.LABELS.YOUR_MESSAGE')}</h3>

                <div className={'flex gap-10'}>
                  <div className={'flex gap-3 items-center'}>
                    <Toggle
                      id={'isEnabledOneOffMessage'}
                      label={t('pages.ADMIN.FORM.LABELS.USE_ONE_OFF')}
                      color={'header'}
                      isOnOff
                      onChange={() => {
                        const newValue = !values.isEnabledOneOffMessage;
                        setFieldValue('isEnabledOneOffMessage', newValue);
                        setFieldValue('messageTab', !newValue ? 0 : 1);
                        setPreviewData((prev) => ({
                          ...prev,
                          message: newValue ? values.oneOffMessage : values.standardMessage,
                        }));
                      }}
                      checked={values.isEnabledOneOffMessage}
                    />
                    <button
                      type={'button'}
                      className={'appearance-none'}
                      onClick={() => setFieldValue('isEnabledOneOffMessage', !values.isEnabledOneOffMessage)}
                    >
                      <div className={`font-bold transition-opacity ${classNames({ 'opacity-50': !values.isEnabledOneOffMessage })}`}>
                        {t('pages.ADMIN.FORM.LABELS.USE_ONE_OFF')}
                      </div>
                    </button>
                  </div>
                </div>

                <Tabs
                  id={'messageTabs'}
                  label={'Tabs'}
                  tabs={[
                    { content: t('pages.ADMIN.FORM.LABELS.STANDARD_MESSAGE') },
                    { content: t('pages.ADMIN.FORM.LABELS.ONE_OFF_MESSAGE') },
                  ]}
                  active={values.messageTab}
                  handleClick={(value) => setFieldValue('messageTab', value)}
                />

                {MessageTab.standard === values.messageTab
                  ? (
                    <Textarea
                      key={'standardMessage'}
                      id={'standardMessage'}
                      label={t('pages.ADMIN.FORM.LABELS.STANDARD_MESSAGE')}
                      hideLabel
                      placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.STANDARD_MESSAGE')}
                      onChange={(event) => setFieldValue('standardMessage', event.target.value)}
                      value={values.standardMessage ?? ''}
                      error={errors.standardMessage}
                    />
                  )
                  : (
                    <Textarea
                      key={'oneOffMessage'}
                      id={'oneOffMessage'}
                      label={t('pages.ADMIN.FORM.LABELS.ONE_OFF_MESSAGE')}
                      hideLabel
                      placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.ONE_OFF_MESSAGE')}
                      onChange={(event) => setFieldValue('oneOffMessage', event.target.value)}
                      value={values.oneOffMessage ?? ''}
                      error={errors.oneOffMessage}
                    />
                  )}

                <hr className="h-px my-4 bg-gray-300 border-0" />

                <h3 className={'font-bold'}>{t('pages.ADMIN.FORM.LABELS.JUST_A_REMINDER')}</h3>

                <div className={'flex gap-10'}>
                  <div className={'flex gap-3 items-center'}>
                    <Toggle
                      id={'isEnabledLearningOutcomeReminderMessage'}
                      label={t('pages.ADMIN.FORM.LABELS.INCLUDE_REMINDER')}
                      color={'header'}
                      isOnOff
                      onChange={() => {
                        const newValue = !values.isEnabledLearningOutcomeReminderMessage;
                        setFieldValue('isEnabledLearningOutcomeReminderMessage', newValue);
                        setPreviewData((prev) => ({
                          ...prev,
                          reminderMessage: newValue ? values.learningOutcomeReminderMessage : null,
                        }));
                      }}
                      checked={values.isEnabledLearningOutcomeReminderMessage}
                    />
                    <button
                      type={'button'}
                      className={'appearance-none'}
                      onClick={() => {
                        const newValue = !values.isEnabledLearningOutcomeReminderMessage;
                        setFieldValue('isEnabledLearningOutcomeReminderMessage', newValue);
                        setPreviewData((prev) => ({
                          ...prev,
                          reminderMessage: newValue ? values.learningOutcomeReminderMessage : null,
                        }));
                      }}
                    >
                      <div className={`font-bold transition-opacity ${classNames({ 'opacity-50': !values.isEnabledLearningOutcomeReminderMessage })}`}>
                        {t('pages.ADMIN.FORM.LABELS.INCLUDE_REMINDER')}
                      </div>
                    </button>
                  </div>
                </div>

                <Textarea
                  id={'learningOutcomeReminderMessage'}
                  label={t('pages.ADMIN.FORM.LABELS.REMINDER_MESSAGE')}
                  hideLabel
                  placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.REMINDER_MESSAGE')}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    setFieldValue('learningOutcomeReminderMessage', newValue);
                    if (values.isEnabledLearningOutcomeReminderMessage) {
                      setPreviewData((prev) => ({ ...prev, reminderMessage: newValue }));
                    }
                  }}
                  value={values.learningOutcomeReminderMessage ?? ''}
                  error={errors.learningOutcomeReminderMessage}
                />

                <hr className="h-px my-4 bg-gray-300 border-0" />

                <h3 className={'font-bold'}>{t('pages.ADMIN.FORM.LABELS.METRICS_COMPONENTS')}</h3>

                <div className={'flex flex-col gap-1'}>
                  <div className={'grid grid-cols-2 gap-5'}>
                    <Selector
                      id={'reportingPeriodFrom'}
                      label={t('pages.ADMIN.FORM.LABELS.DATA_PERIOD_FROM')}
                      options={WEEKDAYS_OPTIONS}
                      onChange={(option: OptionType<number> | null) => setFieldValue('reportingPeriodFrom', option)}
                      value={values.reportingPeriodFrom ?? ''}
                      error={errors.reportingPeriodFrom}
                    />
                    <Selector
                      id={'reportingPeriodTo'}
                      label={t('pages.ADMIN.FORM.LABELS.DATA_PERIOD_TO')}
                      options={WEEKDAYS_OPTIONS}
                      onChange={(option: OptionType<number> | null) => setFieldValue('reportingPeriodTo', option)}
                      value={values.reportingPeriodTo ?? ''}
                      error={errors.reportingPeriodTo}
                    />
                  </div>

                  {(null !== reportingPeriodFromDate && null !== reportingPeriodToDate) && (
                    <div className={'text-sm italic'}>
                      {t('pages.ADMIN.FORM.LABELS.NEXT_EMAIL_PERIOD', { from: reportingPeriodFromDate.toLocaleString(), to: reportingPeriodToDate.toLocaleString() })}
                    </div>
                  )}
                </div>

                <div className={'mt-5 flex flex-col gap-5'}>
                  <FieldArray name={'metricsComponents'}>
                    {({ push, replace, remove }) => (
                      <>
                        {values.metricsComponents.map((component, index) => (
                          <div key={index} className={'mt-2 flex items-center'}>
                            <div className={'flex-grow'}>
                              <span>{t('pages.ADMIN.FORM.LABELS.METRIC_COMPONENT', { number: index + 1 })}</span>
                            </div>
                            <div className={'flex-grow'}>
                              <Selector
                                id={`metricsComponent[${index}]`}
                                label={t('pages.ADMIN.FORM.LABELS.METRIC_COMPONENT', { number: index + 1 })}
                                hideLabel
                                isClearable={false}
                                options={METRICS_COMPONENTS
                                  .filter((mc) => !values.metricsComponents.some((o) => mc === o?.value))
                                  .map((mc) => ({
                                    label: t(`pages.ADMIN.FORM.LABELS.METRICS_COMPONENTS_OPTIONS.${mc}`),
                                    value: mc,
                                  }))
                                }
                                onChange={(option: OptionType<number>) => replace(index, option)}
                                value={values.metricsComponents[index] ?? ''}
                                error={errors.metricsComponents?.[index] ? t('pages.ADMIN.FORM.ERRORS.INVALID_METRIC_COMPONENT') : null}
                              />
                            </div>
                            <ButtonIcon
                              type={'button'}
                              icon={GLOBAL_ICONS.delete}
                              size={'sm'}
                              title={t('pages.ADMIN.FORM.LABELS.DELETE_METRIC_COMPONENT', { number: index + 1 })}
                              onClick={() => remove(index)}
                              asLink
                            />
                          </div>
                        ))}

                        <Button
                          type={'button'}
                          className={'w-full'}
                          isOutline
                          onClick={() => push(null)}
                          disabled={values.metricsComponents.length >= METRICS_COMPONENTS.length}
                        >
                          {t('pages.ADMIN.FORM.LABELS.ADD_METRIC_COMPONENT')}
                        </Button>
                      </>
                    )}
                  </FieldArray>
                </div>

                <hr className="h-px my-4 bg-gray-300 border-0" />

                <div>
                  <Button
                    type={'submit'}
                    color={'positive'}
                    className={'w-full'}
                    disabled={putOrganizationWeeklyLeadersEmailMutation.isLoading}
                  >
                    {t('pages.ADMIN.FORM.LABELS.SAVE')}
                  </Button>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

interface Props {
  email: OrganizationWeeklyLeadersEmail;
  setPreviewData: Dispatch<SetStateAction<PreviewData>>;
}

enum MessageTab {
  standard,
  oneOff,
}

interface FormValues extends Pick<OrganizationWeeklyLeadersEmail, 'standardMessage' | 'oneOffMessage' | 'learningOutcomeReminderMessage'> {
  hour: OptionType<NonNullable<OrganizationWeeklyLeadersEmail['hour']>> | null;
  weekday: OptionType<NonNullable<OrganizationWeeklyLeadersEmail['weekday']>> | null;
  to: (OptionType<number> & { firstName: string })[];
  cc: OptionType<string>[];
  bcc: OptionType<string>[];
  isEnabledOneOffMessage: NonNullable<OrganizationWeeklyLeadersEmail['isEnabledOneOffMessage']>;
  standardGreeting: NonNullable<OrganizationWeeklyLeadersEmail['standardGreeting']>;
  isEnabled: NonNullable<OrganizationWeeklyLeadersEmail['isEnabled']>;
  isEnabledLearningOutcomeReminderMessage: NonNullable<OrganizationWeeklyLeadersEmail['isEnabledLearningOutcomeReminderMessage']>;
  reportingPeriodFrom: OptionType<NonNullable<OrganizationWeeklyLeadersEmail['reportingPeriodFrom']>> | null;
  reportingPeriodTo: OptionType<NonNullable<OrganizationWeeklyLeadersEmail['reportingPeriodTo']>> | null;
  metricsComponents: (OptionType<OrganizationWeeklyLeadersEmail['metricsComponents'][number]> | null)[];
  messageTab: MessageTab;
}

export { OrganizationEmailConfigurationForm };
